import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api, { fmtNum } from "@/lib/api";
import { PageHeader, Card, StatusBadge } from "@/components/kit";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";

export default function Lots() {
  const [selId, setSelId] = useState(null);
  const { data: lots = [], isLoading } = useQuery({
    queryKey: ["lots-view"],
    queryFn: async () => (await api.get("/reports/lots")).data,
  });
  const { data: detail } = useQuery({
    queryKey: ["lot-detail", selId],
    queryFn: async () => (await api.get(`/reports/lots/${selId}`)).data,
    enabled: !!selId,
  });

  return (
    <div data-testid="lots-page">
      <PageHeader title="Lots" subtitle="Identity = Warehouse + Stack + Stock Owner + Stock Item" />
      {isLoading ? (
        <div className="grid place-items-center py-24"><Loader2 className="animate-spin text-emerald-700" size={32} /></div>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-stone-50 text-left text-[11px] uppercase tracking-wide text-stone-500">
                <tr>
                  <th className="px-4 py-3">Lot Number</th><th className="px-4 py-3">Warehouse / Stack</th>
                  <th className="px-4 py-3">Owner</th><th className="px-4 py-3">Item</th>
                  <th className="px-4 py-3 text-right">Bags</th><th className="px-4 py-3 text-right">Weight (Qtl)</th>
                  <th className="px-4 py-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {lots.map((l) => (
                  <tr key={l.id} onClick={() => setSelId(l.id)} data-testid={`lot-row-${l.id}`}
                    className="border-t border-stone-100 cursor-pointer even:bg-stone-50/50 hover:bg-emerald-50/50">
                    <td className="px-4 py-3 font-mono text-xs font-semibold text-emerald-700">{l.lot_number}</td>
                    <td className="px-4 py-3">{l.warehouse_name} / {l.stack_code}</td>
                    <td className="px-4 py-3">{l.stock_owner_name}</td>
                    <td className="px-4 py-3">{l.item_name}</td>
                    <td className="px-4 py-3 text-right font-semibold">{fmtNum(l.current_bags)}</td>
                    <td className="px-4 py-3 text-right">{fmtNum(l.current_weight)}</td>
                    <td className="px-4 py-3"><StatusBadge tone={l.status === "Active" ? "emerald" : "neutral"}>{l.status}</StatusBadge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Dialog open={!!selId} onOpenChange={(o) => !o && setSelId(null)}>
        <DialogContent className="max-w-2xl" data-testid="lot-detail-dialog">
          {detail && (
            <>
              <DialogHeader><DialogTitle className="font-display font-mono text-emerald-700">{detail.lot.lot_number}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Info label="Owner" value={detail.lot.stock_owner_name} />
                <Info label="Item" value={detail.lot.item_name} />
                <Info label="Location" value={`${detail.lot.warehouse_name}/${detail.lot.stack_code}`} />
                <Info label="Current" value={`${fmtNum(detail.lot.current_bags)} bags`} />
                {detail.lot.source_lot_number && <Info label="Source Lot" value={detail.lot.source_lot_number} />}
              </div>
              <div className="mt-3">
                <div className="text-[11px] font-bold uppercase tracking-widest text-stone-400 mb-2">Movement History</div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-stone-50 text-left text-[11px] uppercase text-stone-500">
                      <tr><th className="px-3 py-2">Type</th><th className="px-3 py-2">Ref</th><th className="px-3 py-2 text-right">In</th><th className="px-3 py-2 text-right">Out</th><th className="px-3 py-2 text-right">Balance</th></tr>
                    </thead>
                    <tbody>
                      {detail.movements.map((mv) => (
                        <tr key={mv.id} className="border-t border-stone-100">
                          <td className="px-3 py-2"><StatusBadge tone={mv.txn_type === "INWARD" ? "emerald" : "info"}>{mv.txn_type}</StatusBadge></td>
                          <td className="px-3 py-2 font-mono text-xs">{mv.txn_number}</td>
                          <td className="px-3 py-2 text-right text-emerald-700">{mv.bags_in ? fmtNum(mv.bags_in) : "—"}</td>
                          <td className="px-3 py-2 text-right text-red-600">{mv.bags_out ? fmtNum(mv.bags_out) : "—"}</td>
                          <td className="px-3 py-2 text-right font-semibold">{fmtNum(mv.balance_bags)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Info = ({ label, value }) => (
  <div className="rounded-lg bg-stone-50 p-2.5"><div className="text-[11px] text-stone-500">{label}</div><div className="font-semibold text-stone-900 text-sm truncate">{value}</div></div>
);
